import React, { useEffect, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Portal,
  Spinner,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { MdClose } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { patientService } from 'services/patientService';
import { Patient } from 'types/models';

export function PatientSearchBar(props: {
  h?: string | number;
  ps?: string | number;
  pe?: string | number;
  ms?: string | number;
  me?: string | number;
  mb?: string | number | { [key: string]: any };
  borderRadius?: string | number;
  [x: string]: any;
}) {
  const { h, ps, pe, ...rest } = props;
  const navigate = useNavigate();

  const [query, setQuery]       = useState('');
  const [results, setResults]   = useState<Patient[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen]         = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [rect, setRect]         = useState<DOMRect | null>(null);

  const inputRef    = useRef<HTMLInputElement>(null);
  const listRef     = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const justPicked  = useRef(false);

  const searchIconColor = useColorModeValue('gray.700', 'white');
  const inputBg     = useColorModeValue('secondaryGray.300', 'navy.900');
  const inputText   = useColorModeValue('gray.700', 'gray.100');
  const dropBg      = useColorModeValue('white', 'navy.800');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const hoverBg     = useColorModeValue('brand.50', 'navy.700');
  const activeBg    = useColorModeValue('brand.100', 'navy.600');
  const mutedColor  = useColorModeValue('gray.500', 'gray.400');
  const textColor   = useColorModeValue('gray.800', 'white');
  const emptyColor  = useColorModeValue('gray.400', 'gray.500');

  const openDropdown = () => {
    if (inputRef.current) setRect(inputRef.current.getBoundingClientRect());
    setOpen(true);
    setActiveIdx(-1);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);

    if (!val.trim()) {
      setResults([]);
      setOpen(false);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setIsLoading(true);
      try {
        const data = await patientService.search(val.trim(), 8);
        setResults(data);
        openDropdown();
      } catch {
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);
  };

  const pick = (patient: Patient) => {
    justPicked.current = true;
    setQuery('');
    setResults([]);
    setOpen(false);
    navigate(`/admin/patients/${patient.id}`);
    setTimeout(() => { justPicked.current = false; }, 100);
  };

  const clear = () => {
    setQuery('');
    setResults([]);
    setOpen(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open && results.length > 0) openDropdown();
      setActiveIdx(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && activeIdx >= 0 && results[activeIdx]) {
      e.preventDefault();
      pick(results[activeIdx]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!inputRef.current?.contains(t) && !listRef.current?.contains(t)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  return (
    <Box position='relative' w={{ base: '100%', md: '300px' }} {...rest}>
      <InputGroup h={h}>
        <InputLeftElement h={h} pointerEvents='none'>
          {isLoading
            ? <Spinner size='xs' color='brand.500' />
            : <SearchIcon color={searchIconColor} w='15px' h='15px' />
          }
        </InputLeftElement>
        <Input
          ref={inputRef}
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (!justPicked.current && results.length > 0) openDropdown();
          }}
          variant='search'
          fontSize='sm'
          bg={inputBg}
          color={inputText}
          fontWeight='500'
          h={h}
          ps={ps ?? '40px'}
          pe={query ? '32px' : (pe ?? '12px')}
          _placeholder={{ color: 'gray.400', fontSize: '14px' }}
          borderRadius='100px'
          placeholder='Buscar paciente…'
          autoComplete='off'
        />
        {query && (
          <InputRightElement h={h} cursor='pointer' onClick={clear}>
            <MdClose style={{ color: '#A0AEC0', width: 14, height: 14 }} />
          </InputRightElement>
        )}
      </InputGroup>

      {open && rect && (
        <Portal>
          <Box
            ref={listRef}
            position='fixed'
            top={`${rect.bottom + 6}px`}
            left={`${rect.left}px`}
            width={`${Math.max(rect.width, 340)}px`}
            zIndex={9999}
            bg={dropBg}
            border='1px solid'
            borderColor={borderColor}
            borderRadius='16px'
            boxShadow='0 12px 32px rgba(0,0,0,0.14)'
            maxH='400px'
            overflowY='auto'
            py='6px'
          >
            {results.length === 0 ? (
              <Flex direction='column' align='center' py='28px' gap='6px'>
                <Text fontSize='xs' color={emptyColor} fontWeight='500'>
                  Sin resultados para "{query}"
                </Text>
                <Text fontSize='xs' color={emptyColor}>
                  Prueba con nombre, cédula, teléfono o correo
                </Text>
              </Flex>
            ) : (
              <>
                <Text
                  px='14px' pt='8px' pb='4px'
                  fontSize='9px' fontWeight='700'
                  color={mutedColor} textTransform='uppercase' letterSpacing='0.08em'>
                  {results.length} resultado{results.length !== 1 ? 's' : ''}
                </Text>
                {results.map((patient, idx) => (
                  <Flex
                    key={patient.id}
                    align='center'
                    gap='10px'
                    px='14px'
                    py='9px'
                    cursor='pointer'
                    bg={idx === activeIdx ? activeBg : 'transparent'}
                    _hover={{ bg: hoverBg }}
                    onMouseDown={() => pick(patient)}
                  >
                    <Avatar
                      name={`${patient.nombres} ${patient.apellidos}`}
                      bg='brand.500'
                      color='white'
                      flexShrink={0}
                      w='34px'
                      h='34px'
                      fontSize='xs'
                      fontWeight='700'
                    />
                    <Box flex='1' minW={0}>
                      <Text fontSize='sm' fontWeight='700' color={textColor} noOfLines={1}>
                        {patient.nombres} {patient.apellidos}
                      </Text>
                      <Flex gap='6px' align='center' flexWrap='wrap'>
                        <Text fontSize='xs' color={mutedColor} fontFamily='mono'>
                          {patient.cedula}
                        </Text>
                        {patient.telefono && (
                          <>
                            <Text fontSize='xs' color={mutedColor}>·</Text>
                            <Text fontSize='xs' color={mutedColor}>{patient.telefono}</Text>
                          </>
                        )}
                        {patient.email && (
                          <>
                            <Text fontSize='xs' color={mutedColor}>·</Text>
                            <Text fontSize='xs' color={mutedColor} noOfLines={1} maxW='160px'>
                              {patient.email}
                            </Text>
                          </>
                        )}
                      </Flex>
                    </Box>
                  </Flex>
                ))}
              </>
            )}
          </Box>
        </Portal>
      )}
    </Box>
  );
}
